import { rawListeners } from 'process';
import { IFacilityTargetMargin } from './types';
import { facility } from 'locales/en';
import uuid from 'react-uuid';

export function makeDeterministicId(r: any) {
  return [r.facilityId, r.disciplineId ?? 'NA', r.specialtyId ?? 'NA', r.subSpecialtyId ?? 'NA'].join('-');
}

export const formatSpecialty = (options: { specialtyId: number; disciplineId: number; description: string }[]) =>
  options.map(item => ({
    id: item.specialtyId,
    disciplineId: item.disciplineId,
    description: item.description,
    label: item.description,
    raw: item,
  }));

export const formatSubSpecialty = (options: { description: string; specialtyId: number; subSpecialtyId: number }[]) =>
  options.map(item => ({
    id: item.subSpecialtyId,
    specialtyId: item.specialtyId,
    description: item.description,
    label: item.description,
    raw: item,
  }));

export const formattDiscipline = (
  options: { abbreviation: string; description: string; id: number; name: string | null }[],
) =>
  options.map(item => ({
    id: item.id,
    value: item.abbreviation,
    description: item.description,
    label: item.description,
    raw: item,
  }));

export const disciplineBasedOnDivision = (amnDivision: number, disciplines: any[], divisionDisciplines: any[]) => {
  const formattedDiscipline = [];
  const uniqueDisciplineIds: number[] = [];

  // Filter divisionDisciplines to only include those matching the given amnDivision
  const divisionDisciplineList = divisionDisciplines?.filter(x => x?.amnDivisionId === amnDivision);

  //create a list of unique discipline IDs from the filtered divisionDisciplines
  divisionDisciplineList?.forEach(item => {
    if (!uniqueDisciplineIds?.includes(item?.disciplineId)) {
      uniqueDisciplineIds.push(item?.disciplineId);
    }
  });

  // Map the unique discipline IDs to their corresponding discipline objects and add them to the formattedDiscipline list
  uniqueDisciplineIds?.map(id => {
    disciplines?.map(item => {
      if (item?.id === id) {
        formattedDiscipline.push(item);
      }
    });
  });
  return formattedDiscipline;
};

export const buildSpecialtyByDisciplineLookup = specialties => {
  const lookup = {};

  for (const sp of specialties ?? []) {
    const disciplineId = sp?.disciplineId;
    if (!disciplineId) continue;

    (lookup[disciplineId] ||= []).push(sp);
  }

  return lookup;
};

export const buildSubSpecialtyBySpecialtyLookup = (
  subSpecialties: {
    id: number;
    specialtyId: number;
    description: string;
    label: string;
    raw: any;
  }[],
) => {
  const lookup: Record<number, typeof subSpecialties> = {};

  for (const sub of subSpecialties ?? []) {
    const specialtyId = sub?.specialtyId;
    if (!specialtyId) continue;

    (lookup[specialtyId] ||= []).push(sub);
  }

  return lookup;
};

/**
 * Validates rows and appends `error` object per row.
 * Duplicate rules:
 *  - Missing any of D/S/SS => discipline error only
 *  - D-only duplicates => discipline error
 *  - D+S (SS empty) duplicates => discipline+specialty error
 *  - D+S+SS duplicates => discipline+specialty+subSpecialty error
 * Numeric fields:
 *  - error if null/undefined/""
 */

export interface RowError {
  discipline: boolean;
  specialty: boolean;
  subSpecialty: boolean;

  targetMarginDefault: boolean;
  targetMarginDayShift: boolean;
  targetMarginNightShift: boolean;
  targetMarginDayNightShift: boolean;
  payRangeMinMaxPercentage: boolean;
  defaultWeeklyPay: boolean;

  hasError: boolean;
};

const isEmptyValue = (v: any) => v === null || v === undefined || v === '';

export const validateFacilityTargetMarginRows = (rows: IFacilityTargetMargin[]): IFacilityTargetMargin[] => {
  const n = rows?.length ?? 0;
  const result: IFacilityTargetMargin[] = new Array(n);

  // combo-level maps -> first index of that combo
  const seenAllNull = new Map<string, number>(); // key: "ALL_NULL"
  const seenDOnly = new Map<string, number>(); // key: `D:${d}`
  const seenDS = new Map<string, number>(); // key: `D:${d}|S:${s}`
  const seenDSS = new Map<string, number>(); // key: `D:${d}|S:${s}|SS:${ss}`

  const initError = (row: IFacilityTargetMargin): RowError => ({
    discipline: false,
    specialty: false,
    subSpecialty: false,

    targetMarginDefault: isEmptyValue(row.targetMarginDefault),
    targetMarginDayShift: isEmptyValue(row.targetMarginDayShift),
    targetMarginNightShift: isEmptyValue(row.targetMarginNightShift),
    targetMarginDayNightShift: isEmptyValue(row.targetMarginDayNightShift),
    payRangeMinMaxPercentage: isEmptyValue(row.payRangeMinMaxPercentage),
    defaultWeeklyPay: isEmptyValue(row.defaultWeeklyPay),

    hasError: false,
  });

  // 1) Clone + numeric validations
  for (let i = 0; i < n; i++) {
    const row = rows[i];
    result[i] = { ...row, error: initError(row) };
  }

  // helpers to mark errors on both rows
  const markDiscipline = (a: number, b?: number) => {
    result[a].error.discipline = true;
    if (b !== undefined) result[b].error.discipline = true;
  };

  const markDisciplineSpecialty = (a: number, b?: number) => {
    result[a].error.discipline = true;
    result[a].error.specialty = true;
    if (b !== undefined) {
      result[b].error.discipline = true;
      result[b].error.specialty = true;
    }
  };

  const markAllThree = (a: number, b?: number) => {
    result[a].error.discipline = true;
    result[a].error.specialty = true;
    result[a].error.subSpecialty = true;
    if (b !== undefined) {
      result[b].error.discipline = true;
      result[b].error.specialty = true;
      result[b].error.subSpecialty = true;
    }
  };

  // 2) Combination validations
  for (let i = 0; i < n; i++) {
    const row = result[i];
    const d = row.disciplineId ?? null;
    const s = row.specialtyId ?? null;
    const ss = row.subSpecialtyId ?? null;

    // (1) (null, null, null)
    if (d === null && s === null && ss === null) {
      const key = 'ALL_NULL';
      const firstIdx = seenAllNull.get(key);
      if (firstIdx === undefined) {
        seenAllNull.set(key, i);
      } else {
        // duplicate -> discipline error (both)
        markDiscipline(i, firstIdx);
      }
      continue;
    }

    // (2) (D, null, null)
    if (d !== null && s === null && ss === null) {
      const key = `D:${d}`;
      const firstIdx = seenDOnly.get(key);
      if (firstIdx === undefined) {
        seenDOnly.set(key, i);
      } else {
        // duplicate -> discipline error (both)
        markDiscipline(i, firstIdx);
      }
      continue;
    }

    // (3) (D, S, null)
    if (d !== null && s !== null && ss === null) {
      const key = `D:${d}|S:${s}`;
      const firstIdx = seenDS.get(key);
      if (firstIdx === undefined) {
        seenDS.set(key, i);
      } else {
        // duplicate -> discipline + specialty error (both)
        markDisciplineSpecialty(i, firstIdx);
      }
      continue;
    }

    // (4) (D, S, SS)
    if (d !== null && s !== null && ss !== null) {
      const key = `D:${d}|S:${s}|SS:${ss}`;
      const firstIdx = seenDSS.get(key);
      if (firstIdx === undefined) {
        seenDSS.set(key, i);
      } else {
        // duplicate -> discipline + specialty + subSpecialty error (both)
        markAllThree(i, firstIdx);
      }
      continue;
    }

    // If you can have weird partials like (null, S, null), decide policy.
    // Current policy: ignore combo validation for such cases.
  }

  // 3) hasError
  for (let i = 0; i < n; i++) {
    const e = result[i].error;
    e.hasError =
      e.discipline ||
      e.specialty ||
      e.subSpecialty ||
      e.targetMarginDefault ||
      e.targetMarginDayShift ||
      e.targetMarginNightShift ||
      e.targetMarginDayNightShift ||
      e.payRangeMinMaxPercentage ||
      e.defaultWeeklyPay;
  }

  return result;
};

export const getPayloadForSaveFacilityTargetMargin = (facilityTargetMargins, userid, facilityId) => {
  const formatedPayload = facilityTargetMargins.map(margin => ({
    facilityId: margin.facilityId,
    disciplineId: margin.disciplineId,
    discipline: margin.discipline,
    specialtyId: margin.specialtyId,
    specialty: margin.specialty,
    subSpecialtyId: margin.subSpecialtyId,
    subSpecialty: margin.subSpecialty,
    targetMarginDefault: margin.targetMarginDefault,
    targetMarginDayShift: margin.targetMarginDayShift,
    targetMarginNightShift: margin.targetMarginNightShift,
    targetMarginDayNightShift: margin.targetMarginDayNightShift,
    payRangeMinMaxPercentage: margin.payRangeMinMaxPercentage,
    defaultWeeklyPay: margin.defaultWeeklyPay,
    updateCustomTargetMargin: true,
    updateCustomPayPackage: true,
    mode: margin.mode,
  }));
  return {
    facilityId: facilityId,
    userId: userid,
    facilityTargetMargins: formatedPayload,
  };
};

export function updateMarginsMode(margins: any[], selectedIds: string[], mode: number) {
  return margins.map(margin => {
    if (selectedIds.includes(margin.clientId)) {
      return { ...margin, mode };
    }
    return margin;
  });
}

export function addClientIdsToRows(rows: any[]): any[] {
  return rows.map((r: any) => ({
    ...r,
    clientId: uuid(),
  }));
}

export function processFacilityTargetMarginRows(rows: any[]): any[] {
  const rowsWithClientId = addClientIdsToRows(rows);
  const errorValidatedTargetMargin = validateFacilityTargetMarginRows(rowsWithClientId);
  return errorValidatedTargetMargin;
}

/**
 * Normalize a single row to only persisted/API fields for dirty comparison.
 * Excludes UI-only fields: error, clientId, discipline, specialty, subSpecialty (string versions)
 * Coerces numeric fields to Number or null
 */
export function normalizeRowForDirty(row: IFacilityTargetMargin): Partial<IFacilityTargetMargin> {
  return {
    facilityId: Number(row.facilityId) || null,
    disciplineId: row.disciplineId !== null ? Number(row.disciplineId) : null,
    specialtyId: row.specialtyId !== null ? Number(row.specialtyId) : null,
    subSpecialtyId: row.subSpecialtyId !== null ? Number(row.subSpecialtyId) : null,
    targetMarginDefault: row.targetMarginDefault !== null ? Number(row.targetMarginDefault) : null,
    targetMarginDayShift: row.targetMarginDayShift !== null ? Number(row.targetMarginDayShift) : null,
    targetMarginNightShift: row.targetMarginNightShift !== null ? Number(row.targetMarginNightShift) : null,
    targetMarginDayNightShift: row.targetMarginDayNightShift !== null ? Number(row.targetMarginDayNightShift) : null,
    payRangeMinMaxPercentage: row.payRangeMinMaxPercentage !== null ? Number(row.payRangeMinMaxPercentage) : null,
    defaultWeeklyPay: row.defaultWeeklyPay !== null ? Number(row.defaultWeeklyPay) : null,
    updateCustomTargetMargin: row.updateCustomTargetMargin ?? false,
    updateCustomPayPackage: row.updateCustomPayPackage ?? false,
    mode: row.mode ?? 0,
  };
}

/**
 * Create a deterministic sort key from a normalized row.
 * Includes multiple fields to handle duplicate discipline/specialty/subSpecialty combinations.
 */
export function createSortKey(normalizedRow: Partial<IFacilityTargetMargin>): string {
  const {
    facilityId,
    disciplineId,
    specialtyId,
    subSpecialtyId,
    targetMarginDefault,
    targetMarginDayShift,
    targetMarginNightShift,
    targetMarginDayNightShift,
    payRangeMinMaxPercentage,
    defaultWeeklyPay,
    mode,
  } = normalizedRow;

  return [
    facilityId,
    disciplineId ?? 'null',
    specialtyId ?? 'null',
    subSpecialtyId ?? 'null',
    targetMarginDefault ?? 'null',
    targetMarginDayShift ?? 'null',
    targetMarginNightShift ?? 'null',
    targetMarginDayNightShift ?? 'null',
    payRangeMinMaxPercentage ?? 'null',
    defaultWeeklyPay ?? 'null',
    mode,
  ].join('||');
}

/**
 * Normalize all rows to only persisted/API fields and sort by deterministic key.
 * This ensures order-independent dirty comparison.
 */
export function normalizeRowsForDirty(rows: IFacilityTargetMargin[]): Partial<IFacilityTargetMargin>[] {
  return rows
    .map(normalizeRowForDirty)
    .sort((a, b) => createSortKey(a).localeCompare(createSortKey(b)));
}

/**
 * Check if current rows are dirty compared to original rows.
 * Ignores UI-only fields and is order-independent.
 */
export function isDirtyRows(
  currentRows: IFacilityTargetMargin[],
  originalRows: IFacilityTargetMargin[],
): boolean {
  const a = JSON.stringify(normalizeRowsForDirty(currentRows));
  const b = JSON.stringify(normalizeRowsForDirty(originalRows));
  return a !== b;
}
